import React, { useState, useEffect } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function LugaresTable() {
  const [lugares, setLugares] = useState([]);
  const [filteredLugares, setFilteredLugares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchLugares();
  }, []);

  useEffect(() => {
    filterAndSortLugares();
  }, [searchTerm, lugares, sortColumn, sortDirection]);

  const fetchLugares = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/lugares');
      setLugares(response.data);
      setFilteredLugares(response.data);
    } catch (err) {
      setError('Error al cargar los lugares. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLugares = () => {
    let filtered = lugares.filter((lugare) =>
      lugare.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLugares(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteLugar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este lugar?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/lugares/${id}`);
        fetchLugares();
      } catch (err) {
        setError('Error al eliminar el lugar. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLugares.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredLugares.length / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Sectores</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total sectores" value={lugares.length} icon={<FaUniversity />} color="from-green-500 to-green-700" />
      </div>       

      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Sectores</h2>
          <Link to="/home/lugares/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir sector
          </Link>
        </div>
        
        <div className="flex items-center mb-4">
        <FaSearch className="text-gray-500 mr-2" />
        <input
          type="text"
          placeholder="Buscar por descripción..."
          className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:outline-none focus:ring focus:ring-blue-300"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('id')}>
                  ID
                </th>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('descripcion')}>
                  Descripción
                </th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((lugare) => (
                <tr key={lugare.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{lugare.id}</td>
                  <td className="px-6 py-4">{lugare.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/lugares/${lugare.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/lugares/edit/${lugare.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteLugar(lugare.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 p-4">
          {[...Array(totalPages).keys()].map((num) => (
            <button
              key={num + 1}
              onClick={() => setCurrentPage(num + 1)}
              className={`px-3 py-1 rounded ${currentPage === num + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              {num + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


/* CORRECTO SIN BUSQUEDA 
import React, { useState, useEffect } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function LugaresTable() {
  const [lugares, setLugares] = useState([]);
  const [filteredLugares, setFilteredLugares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchLugares();
  }, []);

  useEffect(() => {
    filterAndSortLugares();
  }, [searchTerm, lugares, sortColumn, sortDirection]);

  const fetchLugares = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/lugares');
      setLugares(response.data);
      setFilteredLugares(response.data);
    } catch (err) {
      setError('Error al cargar los lugares. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLugares = () => {
    let filtered = lugares.filter(lugare =>
      lugare.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLugares(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteLugar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este lugar?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/lugares/${id}`);
        fetchLugares();
      } catch (err) {
        setError('Error al eliminar el lugar. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLugares.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredLugares.length / itemsPerPage);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Sectores</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total sectores" value={lugares.length} icon={<FaUniversity />} color="from-green-500 to-green-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Sectores</h2>
          <Link to="/home/lugares/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir sector
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="px-6 py-3">ID</th>
                <th className="px-6 py-3">Descripción</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((lugare) => (
                <tr key={lugare.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{lugare.id}</td>
                  <td className="px-6 py-4">{lugare.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/lugares/${lugare.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/lugares/edit/${lugare.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteLugar(lugare.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center space-x-2 p-4">
          {[...Array(totalPages).keys()].map((num) => (
            <button key={num + 1} onClick={() => setCurrentPage(num + 1)} className={`px-3 py-1 rounded ${currentPage === num + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>{num + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
*/

/* CORRECTO SIN PAGINADO

import React, { useState, useEffect } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function LugaresTable() {
  const [lugares, setLugares] = useState([]);
  const [filteredLugares, setFilteredLugares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchLugares();
  }, []);

  useEffect(() => {
    filterAndSortLugares();
  }, [searchTerm, lugares, sortColumn, sortDirection]);

  const fetchLugares = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/lugares');
      setLugares(response.data);
      setFilteredLugares(response.data);
    } catch (err) {
      setError('Error al cargar los lugares. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLugares = () => {
    let filtered = lugares.filter(lugare =>
      lugare.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLugares(sorted);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleDeleteLugar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este lugar?')) {
      setIsLoading(true);
      setError(null);
      try {
        await axios.delete(`/lugares/${id}`);
        fetchLugares();
      } catch (err) {
        setError('Error al eliminar el lugar. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLugares.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Sectores</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total sectores" value={lugares.length} icon={<FaUniversity />} color="from-green-500 to-green-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Sectores</h2>
          <Link to="/home/lugares/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir sector
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="text-green-700 mr-2" />
          <input
            type="text"
            placeholder="Buscar por Descripción"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('id')}>ID {sortColumn === 'id' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('descripcion')}>Descripción {sortColumn === 'descripcion' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((lugare) => (
                <tr key={lugare.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{lugare.id}</td>
                  <td className="px-6 py-4">{lugare.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/lugares/${lugare.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/lugares/edit/${lugare.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteLugar(lugare.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
*/

/*
import React, { useState, useEffect, useMemo } from 'react';
import { FaUniversity, FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm">{title}</p>
        <h3 className="text-3xl font-bold">{value}</h3>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </div>
);

export default function LugaresTable() {
  const [lugares, setLugares] = useState([]);
  const [filteredLugares, setFilteredLugares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchLugares();
  }, []);

  useEffect(() => {
    filterAndSortLugares();
  }, [searchTerm, lugares, sortColumn, sortDirection]);

  const fetchLugares = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/lugares');
      setLugares(response.data);
      setFilteredLugares(response.data);
    } catch (err) {
      setError('Error al cargar los lugares. Por favor, intente de nuevo.');
      console.error('Error fetching lugares:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLugares = () => {
    let filtered = lugares.filter(lugare =>
      lugare.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
      if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredLugares(sorted);
    setCurrentPage(1);
  };

  const handleDeleteLugar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este lugar?')) {
      setIsLoading(true);
      setError(null);
      console.log(`Intentando eliminar lugar con ID: ${id}`); // Depuración
  
      try {
        const response = await axios.delete(`/lugares/${id}`);
        console.log('Lugar eliminado con éxito:', response.data); // Verifica respuesta
        fetchLugares(); // Actualizar la lista después de eliminar
      } catch (err) {
        setError('Error al eliminar el lugar. Por favor, intente de nuevo.');
        console.error('Error deleting lugar:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  

  const handleSort = (column) => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLugares.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">Gestión de Sectores</h1>
      <div className="flex justify-center mb-8">
        <StatCard title="Total sectores" value={lugares.length} icon={<FaUniversity />} color="from-green-500 to-green-700" />
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="p-4 flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold">Tabla de Sectores</h2>
          <Link to="/home/lugares/add" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition">
            <FaPlus className="mr-2" /> Añadir sector
          </Link>
        </div>
        <div className="p-4 flex items-center bg-gray-100">
          <FaSearch className="text-green-700 mr-2" />
          <input
            type="text"
            placeholder="Buscar por Descripción"
            className="border p-2 rounded w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('id')}>ID {sortColumn === 'id' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3 cursor-pointer" onClick={() => handleSort('descripcion')}>Descripción {sortColumn === 'descripcion' && (sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />)}</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((lugare) => (
                <tr key={lugare.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">{lugare.id}</td>
                  <td className="px-6 py-4">{lugare.descripcion}</td>
                  <td className="px-6 py-4 flex space-x-2">
                    <Link to={`/home/lugares/${lugare.id}`} className="text-blue-600 hover:text-blue-900"><FaEye /></Link>
                    <Link to={`/home/lugares/edit/${lugare.id}`} className="text-green-600 hover:text-green-900"><FaEdit /></Link>
                    <button onClick={() => handleDeleteLugar(lugare.id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        <div className="flex justify-center space-x-2 my-4">
          {Array.from({ length: Math.ceil(filteredLugares.length / itemsPerPage) }, (_, i) => (
            <button key={i} onClick={() => paginate(i + 1)} className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>{i + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
*/